import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Header from '../Shared/Header';
import Sidebar from '../Shared/Sidebar';
import { requestNotificationPermission } from '../../utils/notificationHelper';
import './StudentDashboard.css';

const iconMap = {
    'Mathematics': { icon: "📐", color: "#3b82f6" },
    'Maths': { icon: "📐", color: "#3b82f6" },
    'Science': { icon: "⚛️", color: "#6366f1" },
    'Physics': { icon: "⚛️", color: "#6366f1" },
    'Chemistry': { icon: "🧪", color: "#10b981" },
    'Biology': { icon: "🧬", color: "#ec4899" },
    'English': { icon: "📖", color: "#f97316" },
    'Hindi': { icon: "🌍", color: "#8b5cf6" },
    'Social Science': { icon: "🌍", color: "#8b5cf6" },
    'Computer Science': { icon: "💻", color: "#6366f1" }
};

const CHART_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const StudentDashboard = () => {
    const { t } = useTranslation(['dashboard', 'common', 'navigation']);
    const navigate = useNavigate();

    const userId = localStorage.getItem('userId') || '1';
    const classId = localStorage.getItem('classId') || '1';
    const studentName = localStorage.getItem('studentName') || 'Aryan Sharma';

    // State for dashboard data
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dashboardStats, setDashboardStats] = useState({
        overall_score: 0,
        score_trend: "0%",
        tests_taken: 0,
        test_trend: "0",
        hours_studied: 0,
        hours_trend: "0h",
        doubts_solved: 0,
        doubts_trend: "0",
        streak_count: 0,
        goal_completion: 0
    });
    const [weeklyPerformance, setWeeklyPerformance] = useState([0, 0, 0, 0, 0, 0, 0]);
    const [activities, setActivities] = useState([]);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [recommendedChapters, setRecommendedChapters] = useState([]);
    const [showExceededModal, setShowExceededModal] = useState(false);

    // Load initial data from cache
    useEffect(() => {
        const cachedData = localStorage.getItem(`dashboard_cache_${userId}`);
        if (cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                setSubjects(parsed.subjects || []);
                setDashboardStats(parsed.dashboardStats || {});
                setWeeklyPerformance(parsed.weeklyPerformance || [0, 0, 0, 0, 0, 0, 0]);
                setActivities(parsed.activities || []);
                setUpcomingEvents(parsed.upcomingEvents || []);
                setRecommendedChapters(parsed.recommendedChapters || []);
                setLoading(false); // Can skip initial loading if we have cache
            } catch (e) {
                console.error("Error parsing dashboard cache:", e);
            }
        }
    }, [userId]);

    const fetchDashboardData = useCallback(async (isRefresh = false) => {
        try {
            if (!isRefresh && !localStorage.getItem(`dashboard_cache_${userId}`)) {
                setLoading(true);
            }
            
            const response = await fetch(`${API_BASE_URL}get_user_progress.php?user_id=${userId}&class_id=${classId}`, { credentials: 'include' });
            const data = await response.json();

            if (data.status === 'success' && data.progress_data) {
                const enhancedSubjects = data.progress_data.map(s => ({
                    ...s,
                    name: s.subject_name,
                    ...(iconMap[s.subject_name] || { icon: "📚", color: "#64748b" }),
                    progress: s.progress,
                    chapters: s.total_chapters
                }));
                
                const stats = data.stats || {};
                const weekly = data.weekly_performance || [0, 0, 0, 0, 0, 0, 0];
                const active = data.recent_activity || [];
                const events = data.upcoming_events || [];
                const recs = data.recommendations || [];

                setSubjects(enhancedSubjects);
                setDashboardStats(stats);
                setWeeklyPerformance(weekly);
                setActivities(active);
                setUpcomingEvents(events);
                setRecommendedChapters(recs);

                // Update cache
                localStorage.setItem(`dashboard_cache_${userId}`, JSON.stringify({
                    subjects: enhancedSubjects,
                    dashboardStats: stats,
                    weeklyPerformance: weekly,
                    activities: active,
                    upcomingEvents: events,
                    recommendedChapters: recs,
                    lastUpdated: Date.now()
                }));
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    }, [userId, classId]);

    useEffect(() => {
        fetchDashboardData();
        requestNotificationPermission(userId);
    }, [fetchDashboardData, userId]);

    const handleStartLearning = useCallback(async (ch) => {
        try {
            const res = await fetch(`${API_BASE_URL}check_package_status.php?user_id=${userId}&chapter_id=${ch.chapter_id}`, { credentials: 'include' });
            const data = await res.json();

            if (data.status === 'success') {
                if (data.exceeded && data.purchase_status !== 'active') {
                    setShowExceededModal(true);
                } else {
                    navigate('/loader', {
                        state: {
                            board: localStorage.getItem('boardName'),
                            class: localStorage.getItem('studentClass'),
                            subject: ch.subject,
                            subject_id: ch.subject_id,
                            chapter: ch.name,
                            chapter_id: ch.chapter_id
                        }
                    });
                }
            } else {
                alert("Unable to verify account status.");
            }
        } catch (err) {
            console.error("Error checking package status:", err);
            alert("Connection error.");
        }
    }, [userId, navigate]);

    const stats_cards = useMemo(() => [
        { label: t('dashboard:dashboard.overallScore', 'Overall Score'), value: `${dashboardStats.overall_score}%`, trend: dashboardStats.score_trend, icon: "🎯", color: "blue" },
        { label: t('dashboard:dashboard.testsTaken', 'Tests Taken'), value: dashboardStats.tests_taken.toString(), trend: dashboardStats.test_trend, icon: "📝", color: "indigo" },
        { label: t('dashboard:dashboard.hoursStudied', 'Hours Studied'), value: `${dashboardStats.hours_studied}h`, trend: dashboardStats.hours_trend, icon: "🕒", color: "orange" },
        { label: t('dashboard:dashboard.doubtsSolved', 'AI Doubts Solved'), value: dashboardStats.doubts_solved.toString(), trend: dashboardStats.doubts_trend, icon: "🤖", color: "purple" }
    ], [dashboardStats, t]);


    // Pre-calculate chart labels to avoid new Date() in render
    const chartLabels = useMemo(() => {
        const labels = [];
        const now = Date.now();
        for (let i = 0; i < 7; i++) {
            const dayIndex = new Date(now - (6 - i) * 24 * 60 * 60 * 1000).getDay();
            labels.push(CHART_DAYS[dayIndex]);
        }
        return labels;
    }, []);

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar active="dashboard" />

                <main className="main-content-area">
                    <Header showActions={true} hidePill={true} />

                    <div className="dashboard-scroll-container">
                        <div className="dashboard-content-v3">
                            {/* Welcome Section */}
                            <div className="welcome-banner">
                                <div className="welcome-text">
                                    <h1>{t('dashboard:dashboard.welcome', { name: studentName, defaultValue: `Welcome back, ${studentName}! 👋` })}</h1>
                                    <p>{t('dashboard:dashboard.goalCompletion', { percent: dashboardStats.goal_completion, defaultValue: `You've completed ${dashboardStats.goal_completion}% of your weekly goals. Keep it up!` })}</p>
                                </div>

                                <div className="welcome-stats">
                                    {(loading && subjects.length === 0) ? (
                                        <div className="streak-card shimmer">
                                            <div className="streak-icon shimmer-placeholder"></div>
                                            <div className="streak-info">
                                                <div className="streak-count shimmer-placeholder"></div>
                                                <div className="streak-label shimmer-placeholder"></div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="streak-card">
                                            <span className="streak-icon">🔥</span>
                                            <div className="streak-info">
                                                <span className="streak-count">{dashboardStats.streak_count}</span>
                                                <span className="streak-label">{t('dashboard:dashboard.dayStreak', 'Day Streak')}</span>
                                            </div>

                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Top Stats Grid */}
                            <div className="stats-grid-v3">
                                {(loading && stats_cards[0].value === "0%") ? (
                                    // Shimmer Loading State
                                    [1, 2, 3, 4].map((i) => (
                                        <div key={i} className="stat-card-v3 shimmer">
                                            <div className="stat-icon shimmer-placeholder"></div>
                                            <div className="stat-details">
                                                <div className="stat-label shimmer-placeholder"></div>
                                                <div className="stat-value shimmer-placeholder"></div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    stats_cards.map((stat, i) => (
                                        <div key={i} className={`stat-card-v3 ${stat.color}`}>
                                            <div className="stat-icon">{stat.icon}</div>
                                            <div className="stat-details">
                                                <span className="stat-label">{stat.label}</span>
                                                <div className="stat-value-group">
                                                    <span className="stat-value">{stat.value}</span>
                                                    <span className="stat-trend">{stat.trend}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="dashboard-main-grid">
                                {/* Left Column */}
                                <div className="dashboard-col-left">
                                    {/* Subjects Progress */}
                                    <section className="dashboard-section-v3">
                                        <div className="section-header-v3">
                                            <h2>📚 {t('dashboard:dashboard.mySubjects', 'My Subjects')}</h2>
                                            <button className="view-all-btn" onClick={() => navigate('/subjects')}>{t('dashboard:dashboard.viewAll', 'View All')}</button>
                                        </div>

                                        <div className="subjects-grid-v3">
                                            {(loading && subjects.length === 0) ? (
                                                <div className="loading-placeholder">Loading subjects...</div>
                                            ) : subjects.map((s, i) => (
                                                <div key={i} className="subject-card-v3">
                                                    <div className="subject-card-header">
                                                        <span className="subject-icon-v3" style={{ background: `${s.color}20`, color: s.color }}>{s.icon}</span>
                                                        <div className="subject-meta">
                                                            <h3>{s.name}</h3>
                                                            <span>{s.chapters} {t('dashboard:dashboard.chapters', 'Chapters')}</span>
                                                        </div>

                                                    </div>
                                                    <div className="subject-progress-area">
                                                        <div className="progress-bar-v3">
                                                            <div className="progress-fill-v3" style={{ width: `${s.progress}%`, background: s.color }}></div>
                                                        </div>
                                                        <span className="progress-percent-v3">{s.progress}%</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    {/* Recommended Pathways */}
                                    <section className="dashboard-section-v3">
                                        <div className="section-header-v3">
                                            <h2>🎯 {t('dashboard:dashboard.smartRecommendations', 'Smart Recommendations')}</h2>
                                        </div>

                                        <div className="recommendations-container-v3">
                                            {recommendedChapters.map((ch, i) => (
                                                <div key={i} className="recommendation-card-v3">
                                                    <div className="rec-left">
                                                        <span className={`status-tag-v3 ${ch.level}`}>{ch.status}</span>
                                                        <div className="rec-info-v3">
                                                            <h4>{ch.name}</h4>
                                                            <p>{ch.subject} • {ch.progress}% completed</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="action-btn-v3"
                                                        onClick={() => handleStartLearning(ch)}
                                                    >
                                                        {t('dashboard:dashboard.startLearning', 'Start Learning')}
                                                    </button>

                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                </div>

                                {/* Right Column */}
                                <div className="dashboard-col-right">
                                    {/* Performance Chart Placeholder */}
                                    <section className={`dashboard-section-v3 glass-card ${(loading && weeklyPerformance.every(v => v === 0)) ? 'shimmer' : ''}`}>
                                        <h3>📈 {t('dashboard:dashboard.weeklyPerformance', 'Weekly Performance')}</h3>

                                        <div className="chart-placeholder-v3">
                                            {(loading && weeklyPerformance.every(v => v === 0)) ? (
                                                <>
                                                    <div className="chart-bars-v3">
                                                        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                                            <div key={i} className="chart-bar-v3 shimmer-placeholder"></div>
                                                        ))}
                                                    </div>
                                                    <div className="chart-labels-v3">
                                                        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                                            <div key={i} className="chart-label-shimmer shimmer-placeholder"></div>
                                                        ))}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="chart-bars-v3">
                                                        {weeklyPerformance.map((h, i) => (
                                                            <div key={i} className="chart-bar-v3" style={{ height: `${h}%` }}>
                                                                <div className="bar-hover-val">{Math.round(h)}%</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="chart-labels-v3">
                                                        {chartLabels.map((label, i) => (
                                                            <span key={i}>{label}</span>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </section>

                                    {/* Recent Activity */}
                                    <section className="dashboard-section-v3 no-bg">
                                        <h3>🔔 {t('dashboard:dashboard.recentActivity', 'Recent Activity')}</h3>

                                        <div className="activity-list-v3">
                                            {activities.map((act, i) => (
                                                <div key={i} className="activity-item-v3">
                                                    <span className={`activity-icon-v3 ${act.type}`}>{act.icon}</span>
                                                    <div className="activity-details-v3">
                                                        <p>{act.title}</p>
                                                        <span>{act.time}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    {/* Upcoming Events */}
                                    <section className="dashboard-section-v3 upcoming-card">
                                        <h3>📅 {t('dashboard:dashboard.upcoming', 'Upcoming')}</h3>

                                        <div className="upcoming-list-v3">
                                            {upcomingEvents.map((ev, i) => (
                                                <div key={i} className="upcoming-item-v3">
                                                    <div className={`event-indicator ${ev.type}`}></div>
                                                    <div className="event-info-v3">
                                                        <p>{ev.title}</p>
                                                        <span>{ev.date}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </main >
            </div >

            {/* Premium Upsell Modal */}
            {showExceededModal && (
                <div className="dr-modal-overlay">
                    <div className="dr-upsell-modal">
                        <div className="dr-modal-header">
                            <div className="dr-modal-icon">💎</div>
                            <h2>{t('dashboard:dashboard.premiumUpsell.title', 'Chapter Limit Reached')}</h2>
                            <button className="dr-modal-close" onClick={() => setShowExceededModal(false)}>✕</button>
                        </div>
                        <div className="dr-modal-body">
                            <p>{t('dashboard:dashboard.premiumUpsell.text', "You've utilized your free unique chapter quota. To continue re-taking tests and unlocking more chapters, upgrade to our Chapter Pack.")}</p>
                            <div className="dr-offer-card">
                                <div className="dr-offer-info">
                                    <span className="dr-offer-title">{t('dashboard:dashboard.premiumUpsell.offerTitle', '8 Premium Chapters')}</span>
                                    <span className="dr-offer-price">{t('dashboard:dashboard.premiumUpsell.offerPrice', 'Only ₹20')}</span>
                                </div>
                                <div className="dr-offer-check">✓</div>
                            </div>
                            <ul className="dr-modal-features">
                                <li>✨ {t('dashboard:dashboard.premiumUpsell.feature1', 'Unlimited Retakes for 8 Chapters')}</li>
                                <li>📊 {t('dashboard:dashboard.premiumUpsell.feature2', 'Advanced AI Growth Insights')}</li>
                                <li>🎯 {t('dashboard:dashboard.premiumUpsell.feature3', 'Personalized Practice Roadmap')}</li>
                            </ul>
                        </div>
                        <div className="dr-modal-footer">
                            <button className="dr-btn-primary full-width" style={{ background: 'linear-gradient(135deg, #00f2ff 0%, #3b82f6 100%)', color: '#000', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px' }} onClick={() => navigate('/pricing')}>
                                {t('dashboard:dashboard.premiumUpsell.button', 'Buy Chapter Pack Now')}
                            </button>
                            <button className="dr-link-btn" onClick={() => setShowExceededModal(false)}>
                                {t('dashboard:dashboard.premiumUpsell.maybeLater', 'Maybe later')}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div >
    );
};

export default StudentDashboard;
